/**
 * Figure: renders a course figure, the declarative diagram of plan
 * docs/plans/lms-plan.md 6.1 ({kind, nodes, edges}), with plain divs and
 * Tailwind: no SVG, no image, no chart library, no raw HTML (D10, D3). WP-15;
 * WP-40's slide layouts place it.
 *
 * The four kinds (the lint's figure rule holds content to these shapes; this
 * component still renders a malformed draft without crashing, for the
 * editor's preview):
 *   flow       nodes in order, joined by arrows; an edge from one node to the
 *              next labels its arrow
 *   cycle      the same, then an arrow back to the first node
 *   compare    the nodes side by side as columns (label, then note)
 *   hierarchy  a tree drawn from the edges (parent to child), roots first
 *
 * Accessibility (6.2 rule 12, G13): the drawing is aria-hidden; a visually
 * hidden figcaption carries the slide's text_equivalent and then the figure
 * read out in order (describeFigure: every node, then every edge), so a
 * screen reader gets the whole figure and color never carries meaning.
 */
import type { ReactElement } from 'react';
import type { FigureEdge, FigureNode, FigureSpec } from '../../content/types';

export interface FigureProps {
  figure: FigureSpec;
  /** The slide's text_equivalent (Appendix E), read before the generated description. */
  textEquivalent?: string | null;
  /** An accessible name for the figure (the slide heading). */
  title?: string;
  className?: string;
}

const KIND_NAMES: Record<FigureSpec['kind'], { name: string; noun: string }> = {
  flow: { name: 'Flow diagram', noun: 'step' },
  cycle: { name: 'Cycle diagram', noun: 'stage' },
  compare: { name: 'Comparison', noun: 'column' },
  hierarchy: { name: 'Hierarchy', noun: 'item' },
};

function labelOf(nodes: FigureNode[], id: string): string {
  const node = nodes.find((n) => n.id === id);
  return node ? node.label : id;
}

function sentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** The figure read out in order: its kind and size, every node, then every edge (6.2 rule 12). */
export function describeFigure(figure: FigureSpec): string {
  const nodes = Array.isArray(figure.nodes) ? figure.nodes : [];
  const edges = Array.isArray(figure.edges) ? figure.edges : [];
  const kind = KIND_NAMES[figure.kind] ?? { name: 'Diagram', noun: 'item' };
  const parts: string[] = [`${kind.name} with ${nodes.length} ${kind.noun}${nodes.length === 1 ? '' : 's'}.`];
  nodes.forEach((node, i) => {
    parts.push(sentence(`${i + 1}. ${node.label}${node.note ? `: ${node.note}` : ''}`));
  });
  edges.forEach((edge) => {
    const from = labelOf(nodes, edge.from);
    const to = labelOf(nodes, edge.to);
    const via = edge.label ? ` (${edge.label})` : '';
    parts.push(figure.kind === 'hierarchy' ? sentence(`${to} is under ${from}${via}`) : sentence(`${from} leads to ${to}${via}`));
  });
  if (figure.kind === 'cycle' && nodes.length > 0) {
    parts.push(sentence(`After ${nodes[nodes.length - 1].label}, the cycle returns to ${nodes[0].label}`));
  }
  return parts.join(' ');
}

const NODE_CLS =
  'min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const BADGE_CLS =
  'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-300';
const NOTE_CLS = 'mt-0.5 text-xs text-gray-500 dark:text-gray-400';
const EDGE_LABEL_CLS = 'text-xs text-gray-500 dark:text-gray-400';

function NodeBox({ node, index }: { node: FigureNode; index?: number }) {
  return (
    <div className={NODE_CLS} data-testid="lms-figure-node" data-node-id={node.id}>
      <div className="flex items-start gap-2">
        {index !== undefined && <span className={BADGE_CLS}>{index + 1}</span>}
        <div className="min-w-0">
          <div className="font-medium">{node.label}</div>
          {node.note && <div className={NOTE_CLS}>{node.note}</div>}
        </div>
      </div>
    </div>
  );
}

/** A CSS arrow: down on narrow screens, right from sm: up (a line and a border triangle). */
function Arrow({ label }: { label?: string | null }) {
  return (
    <div className="flex flex-shrink-0 items-center justify-center gap-1.5 py-0.5 sm:px-1 sm:py-0" data-testid="lms-figure-arrow">
      <span className="flex flex-col items-center sm:hidden">
        <span className="block h-4 w-px bg-gray-400 dark:bg-gray-500" />
        <span className="block h-0 w-0 border-x-[4px] border-t-[6px] border-x-transparent border-t-gray-400 dark:border-t-gray-500" />
      </span>
      <span className="hidden items-center sm:flex">
        <span className="block h-px w-5 bg-gray-400 dark:bg-gray-500" />
        <span className="block h-0 w-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-gray-400 dark:border-l-gray-500" />
      </span>
      {label && <span className={EDGE_LABEL_CLS}>{label}</span>}
    </div>
  );
}

function edgeLabel(edges: FigureEdge[], from: string, to: string): string | null {
  const edge = edges.find((e) => e.from === from && e.to === to);
  return edge && edge.label ? edge.label : null;
}

function Sequence({ nodes, edges, cycle }: { nodes: FigureNode[]; edges: FigureEdge[]; cycle: boolean }) {
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  return (
    <div className="flex flex-col items-stretch gap-1 sm:flex-row sm:flex-wrap sm:items-center">
      {nodes.map((node, i) => (
        <div key={`${i}-${node.id}`} className="flex flex-col items-stretch gap-1 sm:flex-row sm:items-center">
          {i > 0 && <Arrow label={edgeLabel(edges, nodes[i - 1].id, node.id)} />}
          <NodeBox node={node} index={i} />
        </div>
      ))}
      {cycle && first && last && (
        <div className="flex items-center gap-1.5 sm:pl-1" data-testid="lms-figure-return">
          <Arrow label={edgeLabel(edges, last.id, first.id)} />
          <span className={`${EDGE_LABEL_CLS} font-medium`}>{`Back to 1: ${first.label}`}</span>
        </div>
      )}
    </div>
  );
}

const COMPARE_GRID: Record<number, string> = {
  1: 'grid gap-3',
  2: 'grid gap-3 sm:grid-cols-2',
  3: 'grid gap-3 sm:grid-cols-3',
  4: 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4',
};

function Compare({ nodes }: { nodes: FigureNode[] }) {
  return (
    <div className={COMPARE_GRID[Math.min(Math.max(nodes.length, 1), 4)] ?? COMPARE_GRID[4]}>
      {nodes.map((node, i) => (
        <div
          key={`${i}-${node.id}`}
          className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
          data-testid="lms-figure-node"
          data-node-id={node.id}
        >
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{node.label}</div>
          {node.note && <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">{node.note}</div>}
        </div>
      ))}
    </div>
  );
}

function Hierarchy({ nodes, edges }: { nodes: FigureNode[]; edges: FigureEdge[] }) {
  const ids = new Set(nodes.map((n) => n.id));
  const incoming = new Set(edges.filter((e) => ids.has(e.from)).map((e) => e.to));
  let roots = nodes.filter((n) => !incoming.has(n.id));
  if (roots.length === 0 && nodes.length > 0) roots = [nodes[0]];
  const drawn = new Set<string>();

  const renderNode = (node: FigureNode, via: string | null, depth: number): ReactElement | null => {
    if (drawn.has(node.id) || depth > 12) return null;
    drawn.add(node.id);
    const children = edges
      .filter((e) => e.from === node.id)
      .map((e) => ({ edge: e, child: nodes.find((n) => n.id === e.to) }))
      .filter((c): c is { edge: FigureEdge; child: FigureNode } => Boolean(c.child));
    const rendered = children.map(({ edge, child }) => renderNode(child, edge.label ?? null, depth + 1)).filter(Boolean);
    return (
      <div key={node.id} className="space-y-2">
        {via && <div className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">{via}</div>}
        <NodeBox node={node} />
        {rendered.length > 0 && (
          <div className="ml-3 space-y-2 border-l-2 border-gray-200 pl-4 dark:border-gray-700">{rendered}</div>
        )}
      </div>
    );
  };

  const trees = roots.map((root) => renderNode(root, null, 0));
  // A node no root reaches (a malformed draft) is still drawn, after the trees.
  const orphans = nodes.filter((n) => !drawn.has(n.id)).map((n) => renderNode(n, null, 0));
  return <div className="space-y-3">{[...trees, ...orphans]}</div>;
}

export function Figure({ figure, textEquivalent, title, className }: FigureProps) {
  const nodes = Array.isArray(figure.nodes) ? figure.nodes : [];
  const edges = Array.isArray(figure.edges) ? figure.edges : [];
  let drawing: ReactElement;
  switch (figure.kind) {
    case 'compare':
      drawing = <Compare nodes={nodes} />;
      break;
    case 'hierarchy':
      drawing = <Hierarchy nodes={nodes} edges={edges} />;
      break;
    case 'cycle':
      drawing = <Sequence nodes={nodes} edges={edges} cycle />;
      break;
    case 'flow':
    default:
      drawing = <Sequence nodes={nodes} edges={edges} cycle={false} />;
      break;
  }
  return (
    <figure
      className={`w-full ${className ?? ''}`.trim()}
      data-testid="lms-figure"
      data-kind={figure.kind}
      aria-label={title || undefined}
    >
      <div aria-hidden="true" data-testid="lms-figure-drawing">
        {drawing}
      </div>
      <figcaption className="sr-only" data-testid="lms-figure-text">
        {textEquivalent ? <span>{`${sentence(textEquivalent)} `}</span> : null}
        <span>{describeFigure({ kind: figure.kind, nodes, edges })}</span>
      </figcaption>
    </figure>
  );
}

export default Figure;
